import {
  DailyEntry, DailyRecord, DailyRoad, DailyRoadData, DailyRoadState, DailyRoadStateJson, LevelModifiers, LiveEventData, PaidReward,
  dailyEndsAt, dailyRoad, dailyScore, dailyUnlocked, eventAt, eventsUnlocked, metaSeed, payReward, utcDay,
} from "@highmarch/domain";
import { HasSections, registerSaveSection } from "./saveSections";
import { GameEventHub, gameEvents } from "./gameEvents";
import type { LevelCatalog } from "./levelCatalog";

/**
 * Daily Road (MP-20, docs/MODES.md §4) as use cases: today's road (the same for every player), the run that starts it,
 * what a finished run scores and pays — the day's first win pays the data's essence and Marks and its embers to the
 * live event's track (the event-progress path); later runs that day are practice — and the local results history. The
 * state is the `dailyRoad` save section (domain DailyRoadState), bound by the feature that owns it. A finished run is
 * announced on the app's game events (`dailyRoadCompleted`) for the season pass to award pass XP.
 */

function asObject<T>(v: unknown): T | undefined {
  return typeof v === "object" && v !== null && !Array.isArray(v) ? (v as T) : undefined;
}

export const dailyRoadSection = registerSaveSection<DailyRoadStateJson>("dailyRoad", asObject, (v) => v);

export function dailyRoadFromSave(save: HasSections): DailyRoadState { return DailyRoadState.fromJson(dailyRoadSection.read(save)); }

/** Seed tag of the day's reward (it holds no chest today; a later reward with one rolls it from here). */
const DAILY_REWARD_TAG = "dailyRoad";

/** A finished Daily Road run, as the end screen reports it. `key` identifies the run (it counts once). */
export interface FinishedDailyRun {
  readonly modifiers: LevelModifiers;
  readonly level: number;
  readonly won: boolean;
  readonly stagesCleared: number;
  /** Fortress HP left. */
  readonly baseHp: number;
  /** Road tiles laid. */
  readonly road: number;
  readonly key: string;
  readonly now: number;
}

/** What it scored and earned. */
export interface DailyOutcome {
  readonly day: number;
  readonly score: number;
  readonly won: boolean;
  readonly record: DailyRecord;
  /** The day's reward, when this run was its paid one. */
  readonly paid: PaidReward | null;
  /** Embers it added to the live event's track (0 without an event, or for a practice run). */
  readonly embers: number;
  /** The road's day is paid (by this run or an earlier one): this run was practice unless `paid`. */
  readonly dayPaid: boolean;
  readonly event: LiveEventData | null;
}

export class DailyRoadMode {
  constructor(private readonly catalog: LevelCatalog, readonly state: DailyRoadState, private readonly events: GameEventHub = gameEvents) {}

  get data(): DailyRoadData { return this.catalog.balance.modes.daily; }
  /** Open after level `unlockAfterLevel` (the Weekly Trial's). */
  get open(): boolean { return dailyUnlocked(this.data, this.catalog.highestLevelCleared); }
  get unlockAfterLevel(): number { return this.data.unlockAfterLevel; }

  /** Today's road (UTC). */
  today(now: number): DailyRoad { return dailyRoad(this.catalog.balance, utcDay(now)); }
  /** When today's road gives way to tomorrow's. */
  endsAt(now: number): number { return dailyEndsAt(utcDay(now)); }
  /** Today's reward is paid (a win today): further runs are practice. */
  paidToday(now: number): boolean { return this.state.paid(utcDay(now)); }
  /** Today's best run, or null before the first. */
  best(now: number): DailyEntry | null { return this.state.entry(utcDay(now)); }
  /** The kept days, newest first (the last `historyDays`). */
  history(now: number): readonly DailyEntry[] { this.state.prune(utcDay(now), this.data.historyDays); return this.state.entries; }
  /** The live event whose track today's win would add embers to, or null. */
  liveEvent(now: number): LiveEventData | null {
    const ev = this.catalog.balance.events;
    return eventsUnlocked(ev, this.catalog.highestLevelCleared) ? eventAt(ev, now) : null;
  }

  /** Whether `modifiers` are a Daily Road run. */
  isRun(modifiers: LevelModifiers): boolean { return modifiers.daily !== undefined; }

  /**
   * A run ended: its score, the day's best, and — the day's first win — the reward and the event's embers. Announced
   * once on the game events (`dailyRoadCompleted`). Safe to call again for the same run: nothing pays or counts twice.
   */
  finishRun(r: FinishedDailyRun): DailyOutcome | null {
    if (!r.modifiers.daily) return null;
    const day = r.modifiers.daily.day, today = utcDay(r.now), c = this.catalog;
    const score = dailyScore(this.data, { won: r.won, stagesCleared: r.stagesCleared, baseHp: r.won ? r.baseHp : 0 });
    const record = this.state.record(day, today, r.key, { score, won: r.won, stages: r.stagesCleared, hp: r.won ? r.baseHp : 0, road: r.road }, this.data.historyDays);
    let paid: PaidReward | null = null, embers = 0;
    const event = this.liveEvent(r.now);
    if (record.rewarded) {
      const ref = `daily.${day}`;
      paid = payReward(c.balance, this.data.reward, { profile: c.profile, wallet: c.economy.wallet, level: c.highestLevelCleared, seed: metaSeed(c.playerId, DAILY_REWARD_TAG, day), at: r.now, ref });
      if (event && this.data.embers > 0) embers = c.endgame.events.creditBattle(event, ref, this.data.embers);
    }
    if (record.counted) this.events.emit("dailyRoadCompleted", { day, level: r.level, won: r.won, score, rewarded: record.rewarded, newBest: record.newBest, key: r.key, at: r.now });
    return { day, score, won: r.won, record, paid, embers, dayPaid: this.state.paid(day), event: embers > 0 ? event : null };
  }
}
